import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { Logo } from "@/components/Logo";
import { ArrowRight, Lock, Mail, ShieldCheck } from "lucide-react";

function formatErr(e) {
  if (e?.code) {
    const map = {
      "auth/invalid-credential": "Invalid email or password",
      "auth/invalid-email": "That email doesn't look right",
      "auth/user-not-found": "No account with that email",
      "auth/wrong-password": "Wrong password",
      "auth/too-many-requests": "Too many attempts. Try again later",
      "auth/popup-closed-by-user": "Sign-in cancelled",
      "auth/network-request-failed": "Network issue. Check your connection",
      "auth/unauthorized-domain": "This domain isn't authorised in Firebase yet. Let your account manager know.",
    };
    return map[e.code] || e.message || e.code;
  }
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return String(d);
}

export default function LoginPage() {
  const { user, loginFirebase, firebaseEnabled } = useAuth();
  const { tenant } = useTenant();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const nav = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await loginFirebase(email.trim().toLowerCase(), password);
      nav("/", { replace: true });
    } catch (err) {
      setError(formatErr(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-[hsl(var(--background))] text-[hsl(var(--text))]">
      <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
      <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.18), transparent 60%)" }} />
      <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.12), transparent 60%)" }} />

      <div className="relative z-10 min-h-screen grid lg:grid-cols-2">
        {/* Left */}
        <div className="hidden lg:flex flex-col justify-between p-12">
          <Logo size={32} />
          <div>
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-[hsl(var(--primary-muted))] text-[hsl(var(--primary))] text-[11px] font-semibold uppercase tracking-widest mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))]" />
              {tenant.name} · your client portal
            </div>
            <h1 className="text-5xl font-bold tracking-tight leading-[1.05] max-w-lg">
              Where it all<br />
              <span className="text-[hsl(var(--primary))]">clicks.</span>
            </h1>
            <p className="mt-6 text-[15px] text-[hsl(var(--text-muted))] max-w-md leading-relaxed">
              Your projects, invoices, quotes, apps, and the software we run for you. Live, always up to date, always yours to see.
            </p>
            <p className="mt-4 text-[13px] text-[hsl(var(--text-subtle))] max-w-md leading-relaxed italic">
              Built around the way we already work together. Nothing extra, nothing missing.
            </p>
            <div className="mt-10 grid grid-cols-2 gap-4 max-w-md">
              {[
                { k: "Projects & invoices", v: "Real-time" },
                { k: "Secured by Firebase", v: "Just for you" },
              ].map((s) => (
                <div key={s.k} className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-4">
                  <div className="text-[15px] font-semibold tracking-tight">{s.v}</div>
                  <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--text-muted))] mt-1">{s.k}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="text-[11px] text-[hsl(var(--text-subtle))] uppercase tracking-widest">© 2026 {tenant.powered_by || tenant.name}{tenant.powered_by ? " Labs" : ""} · Secured by Firebase</div>
        </div>

        {/* Right */}
        <div className="flex items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-md">
            <div className="lg:hidden mb-8 flex justify-center"><Logo size={32} /></div>
            <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] shadow-[0_40px_80px_-30px_hsl(var(--primary)/0.25)] p-8">
              <div className="mb-6">
                <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--text-muted))]">Welcome back</div>
                <h2 className="text-[26px] font-bold tracking-tight mt-1">Sign in to {tenant.name}</h2>
              </div>

              {firebaseEnabled ? (
                <>
                  <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                    <div>
                      <label className="text-[12px] font-semibold text-[hsl(var(--text-muted))]">Email</label>
                      <div className="mt-1 relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--text-subtle))]" strokeWidth={1.6} />
                        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full pl-10 pr-3 h-11 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] outline-none focus:border-[hsl(var(--primary))] focus:ring-2 focus:ring-[hsl(var(--primary)/0.2)] text-[14px]" data-testid="login-email"/>
                      </div>
                    </div>
                    <div>
                      <label className="text-[12px] font-semibold text-[hsl(var(--text-muted))]">Password</label>
                      <div className="mt-1 relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--text-subtle))]" strokeWidth={1.6} />
                        <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full pl-10 pr-3 h-11 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] outline-none focus:border-[hsl(var(--primary))] focus:ring-2 focus:ring-[hsl(var(--primary)/0.2)] text-[14px]" data-testid="login-password"/>
                      </div>
                    </div>
                    {error && (
                      <div className="text-[12px] text-[hsl(var(--danger))] bg-[hsl(var(--danger-muted))] border border-[hsl(var(--danger)/0.2)] rounded-lg px-3 py-2" data-testid="login-error">{error}</div>
                    )}
                    <button type="submit" disabled={loading} className="w-full h-11 rounded-lg bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-hover))] text-white font-semibold text-[14px] flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:opacity-60 shadow-[0_8px_24px_-6px_hsl(var(--primary)/0.6)]" data-testid="login-submit">
                      {loading ? "Signing in…" : (<>Sign in <ArrowRight className="w-4 h-4" strokeWidth={2}/></>)}
                    </button>
                  </form>

                  <div className="mt-6 pt-5 border-t border-[hsl(var(--border))] text-[11px] text-[hsl(var(--text-muted))] leading-relaxed">
                    <div className="flex items-center gap-2 mb-1">
                      <ShieldCheck className="w-3 h-3 text-[hsl(var(--success))]" strokeWidth={2}/>
                      <span className="font-semibold text-[hsl(var(--text))]">Secured by Firebase</span>
                    </div>
                    Forgot your password? Email your account manager and we'll send a reset link.
                  </div>
                </>
              ) : (
                <div className="rounded-lg border border-[hsl(var(--danger)/0.3)] bg-[hsl(var(--danger-muted))] p-4 text-[12.5px] text-[hsl(var(--text))] leading-relaxed">
                  <div className="font-semibold mb-1">Sign-in is unavailable.</div>
                  Firebase isn't configured for this environment yet. Please contact your account manager.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
